<template>
  <div class="input-profile-config space-y-4 text-xs">
    <div class="flex items-center justify-between">
      <span class="text-[10px] uppercase font-black tracking-widest opacity-70">
        {{ rawMode ? 'Raw JSON' : 'Connection settings' }}
      </span>
      <button
        type="button"
        class="text-[9px]! bg-[#ffaa00]/15! hover:bg-[#ffaa00]/25! text-[#e08c00]! px-3! py-1! rounded-sm! font-remove uppercase! tracking-widest! border! border-[#ffaa00]/40!"
        @click="toggleRawMode"
      >
        {{ rawMode ? 'Form editor' : 'Edit JSON' }}
      </button>
    </div>

    <!-- RAW JSON -->
    <div v-if="rawMode" class="space-y-1.5">
      <textarea
        v-model="rawValue"
        rows="12"
        spellcheck="false"
        class="w-full! resize-y rounded-sm! border! bg-transparent! px-2.5! py-2! font-mono text-xs! text-current! outline-none!"
        :class="rawError ? 'border-[#ff8a65]!' : 'border-gray-500/40! focus:border-[#ffaa00]!'"
        @input="emitRaw"
      ></textarea>
      <span v-if="rawError" class="text-xs text-[#ff8a65]">{{ rawError }}</span>
    </div>

    <!-- FORM -->
    <div v-else class="space-y-4">
      <div class="grid grid-cols-2! gap-4">
        <div class="space-y-1.5">
          <label class="block text-[10px] uppercase font-black tracking-widest opacity-70 leading-none mb-1">Protocol</label>
          <select v-model="config.protocol" :class="fieldClass" @change="emitUpdate">
            <option v-for="protocol in protocols" :key="protocol" :value="protocol">{{ protocol }}</option>
          </select>
        </div>
        <div class="space-y-1.5" v-if="config.protocol === 'SRT'">
          <label class="block text-[10px] uppercase font-black tracking-widest opacity-70 leading-none mb-1">Mode</label>
          <select v-model="config.mode" :class="fieldClass" @change="emitUpdate">
            <option value="CALLER">CALLER</option>
            <option value="LISTENER">LISTENER</option>
          </select>
        </div>
      </div>

      <!-- CONNECTIONS -->
      <div class="rounded-sm p-4 border border-gray-500/25 bg-gray-500/10">
        <div class="flex items-center justify-between mb-3">
          <span class="text-[10px] uppercase font-black tracking-widest opacity-70">
            Connections ({{ config.connection.length }})
          </span>
          <button
            type="button"
            class="text-[9px]! bg-[#ffaa00]/15! hover:bg-[#ffaa00]/25! text-[#e08c00]! px-3! py-1! rounded-sm! font-remove uppercase! tracking-widest! border! border-[#ffaa00]/40!"
            @click="addConnection"
          >
            + Add connection
          </button>
        </div>

        <div v-if="config.connection.length" class="space-y-3">
          <div
            v-for="(connection, index) in config.connection"
            :key="index"
            class="grid grid-cols-[1fr_1.5fr_1.5fr_auto]! items-end gap-3"
          >
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase font-black tracking-widest opacity-70 mb-1">Port</label>
              <input
                v-model.number="connection.port"
                type="number"
                :class="fieldClass"
                @input="emitUpdate"
              />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase font-black tracking-widest opacity-70 mb-1">
                IP {{ config.mode === 'LISTENER' && config.protocol === 'SRT' ? '(whitelist)' : '' }}
              </label>
              <input
                v-model="connection.ip"
                type="text"
                placeholder="empty = any"
                :class="fieldClass"
                @input="emitUpdate"
              />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase font-black tracking-widest opacity-70 mb-1">Passphrase</label>
              <input
                v-model="connection.passphrase"
                type="text"
                :class="fieldClass"
                @input="emitUpdate"
              />
            </div>
            <ma-delete class="bly-delete ma-btn mb-1.5" @click="removeConnection(index)"></ma-delete>
          </div>
        </div>
        <div v-else class="text-center py-2 text-[10px] uppercase font-black tracking-widest opacity-50">
          No connections
        </div>
      </div>

      <!-- PARAMETERS -->
      <div class="rounded-sm p-4 border border-gray-500/25 bg-gray-500/10 space-y-3">
        <span class="text-[10px] uppercase font-black tracking-widest opacity-70">Parameters</span>
        <div class="grid grid-cols-3! gap-3">
          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase font-black tracking-widest opacity-70 mb-1">Latency (ms)</label>
            <input
              v-model.number="config.parameters.latency"
              type="number"
              :class="fieldClass"
              @input="emitUpdate"
            />
          </div>
          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase font-black tracking-widest opacity-70 mb-1">Bitrate (bps)</label>
            <input
              v-model.number="config.parameters.bitrate"
              type="number"
              :class="fieldClass"
              @input="emitUpdate"
            />
            <span class="block text-[10px] opacity-60">{{ bitrateMbps }}</span>
          </div>
          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase font-black tracking-widest opacity-70 mb-1">Routing scope</label>
            <select v-model="config.parameters.routingScope" :class="fieldClass" @change="emitUpdate">
              <option v-for="scope in routingScopes" :key="scope" :value="scope">{{ scope }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  parseInputProfileConfig,
  normalizeInputProfileConfig,
} from '@/services/inputProfileConfig';

// theme-neutral: the editor renders both in the dark permissions modal and in the light
// event preview panel, so colours inherit instead of being pinned to one palette
const FIELD_CLASS = 'h-8! w-full! rounded-sm! border! border-gray-500/40! bg-transparent! px-2! text-xs! text-current! outline-none! focus:border-[#ffaa00]!';

export default {
  name: 'InputProfileConfigEditor',
  props: {
    value: {
      type: [Object, String],
      default: () => ({}),
    },
    protocols: {
      type: Array,
      default: () => ['SRT', 'RTP', 'RIST'],
    },
    routingScopes: {
      type: Array,
      default: () => ['GLOBAL', 'REGIONAL'],
    },
  },
  data() {
    return {
      config: normalizeInputProfileConfig(parseInputProfileConfig(this.value)),
      rawMode: false,
      rawValue: typeof this.value === 'string' ? this.value : JSON.stringify(this.value || {}, null, 2),
      rawError: '',
      fieldClass: FIELD_CLASS,
    };
  },
  computed: {
    bitrateMbps() {
      const bitrate = Number(this.config.parameters.bitrate);
      if (!bitrate) return '';
      return `${(bitrate / 1000000).toFixed(2)} Mbps`;
    },
  },
  methods: {
    emitUpdate() {
      this.$emit('input', JSON.stringify(this.config));
    },
    emitRaw() {
      try {
        const parsed = JSON.parse(this.rawValue);
        this.rawError = '';
        this.config = normalizeInputProfileConfig(parsed);
        this.$emit('input', JSON.stringify(parsed));
      } catch (error) {
        this.rawError = 'Invalid JSON';
      }
    },
    toggleRawMode() {
      if (!this.rawMode) {
        this.rawValue = JSON.stringify(this.config, null, 2);
        this.rawError = '';
      }
      this.rawMode = !this.rawMode;
    },
    addConnection() {
      this.config.connection.push({ port: null, ip: '', passphrase: '' });
      this.emitUpdate();
    },
    removeConnection(index) {
      this.config.connection.splice(index, 1);
      this.emitUpdate();
    },
  },
};
</script>
